import { useCallback, useEffect, useState } from 'react';

import { isLoginSecretStorageUnlockActive } from 'contexts/MatrixContext/loginSecretStorageUnlock';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import logger from 'utils/logging/faro';

const useHasSecurityCode = () => {
  const { matrixClient, matrixClientRef } = useMatrixContext({ requireDefinedMatrixClient: false });
  // null = not yet determined; false = confirmed no security code; true = confirmed has one
  const [hasSecurityCode, setHasSecurityCode] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const checkHasSecurityCode = useCallback(async (): Promise<boolean | null> => {
    setIsLoading(true);
    try {
      const isLoginUnlock = isLoginSecretStorageUnlockActive();
      const client = matrixClient ?? (isLoginUnlock ? matrixClientRef.current : null);

      if (!client) {
        setHasSecurityCode(null);
        return null;
      }

      const defaultKeyId = await client.secretStorage.getDefaultKeyId();
      if (!defaultKeyId) {
        setHasSecurityCode(false);
        return false;
      }

      const keyTuple = await client.secretStorage.getKey(defaultKeyId);
      const hasPassphrase = !!keyTuple?.[1]?.passphrase;
      setHasSecurityCode(hasPassphrase);
      return hasPassphrase;
    } catch (err) {
      logger.error('[useHasSecurityCode] Failed to check for PIN', err);
      setHasSecurityCode(false);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [matrixClient, matrixClientRef]);

  useEffect(() => {
    checkHasSecurityCode();
  }, [checkHasSecurityCode]);

  return { hasSecurityCode, isLoading, refreshHasSecurityCode: checkHasSecurityCode };
};

export default useHasSecurityCode;
